import * as THREE from 'three';

import type { MazeLayout, WallSegment } from './mazeLayout';
import { COLORS, slotTierColor } from './constants';
import { SCORE_ROW, slotsLeftToRight } from './scoreRow';
import {
  createCoverMaterial,
  createHubMaterial,
  createMatteMaterial,
  createMetalMaterial,
  createNeonMaterial,
  createOuterRingMaterial,
  createPlateMaterial,
  createRingWallMaterial,
  createSlotPanelMaterial,
} from '../scene/materials';
import { DEG2RAD } from '../utils/math';

/** Wall segments are grouped by appearance so each group becomes one InstancedMesh. */
function buildWallMeshes(layout: MazeLayout, sharedBox: THREE.BoxGeometry): THREE.Object3D[] {
  const groups = new Map<string, { material: THREE.Material; segments: WallSegment[] }>();

  for (const segment of layout.segments) {
    const key = `${segment.color}:${segment.emissiveIntensity}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        material: createRingWallMaterial(segment.color, segment.emissiveIntensity),
        segments: [],
      };
      groups.set(key, group);
    }
    group.segments.push(segment);
  }

  const meshes: THREE.Object3D[] = [];
  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const zAxis = new THREE.Vector3(0, 0, 1);

  for (const group of groups.values()) {
    const mesh = new THREE.InstancedMesh(sharedBox, group.material, group.segments.length);
    mesh.name = 'maze-walls';

    group.segments.forEach((segment, index) => {
      position.set(segment.x, segment.y, segment.z);
      quaternion.setFromAxisAngle(zAxis, segment.angleRad);
      scale.set(segment.length, segment.thickness, segment.depth);
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(index, matrix);
    });

    mesh.instanceMatrix.needsUpdate = true;
    // The assembly rotates every frame, so per-mesh culling would be wrong.
    mesh.frustumCulled = false;
    meshes.push(mesh);
  }

  return meshes;
}

/**
 * Build the maze's visual geometry procedurally from the SAME layout data used
 * for physics.
 *
 * ---------------------------------------------------------------------------
 * THIS IS A FALLBACK, NOT THE SHIPPING PATH
 * ---------------------------------------------------------------------------
 * Since Phase 3 the board the player sees is `maze_assembly.glb`, loaded at
 * runtime. This builder is kept for two reasons:
 *
 *  1. It is the recovery path if the GLB is missing or corrupt. `GameAssets`
 *     marks that outcome explicitly (`usedFallback: true`), logs it and surfaces
 *     it in the HUD - it is never a silent substitution.
 *  2. It is the reference the alignment test measures the GLB against, because
 *     both are generated from the same layout records.
 *
 * Generating both from one source is what guarantees the walls the player sees
 * are exactly the walls the ball collides with.
 */
export function buildFallbackMazeGroup(layout: MazeLayout): THREE.Group {
  const group = new THREE.Group();
  group.name = 'maze-assembly';

  const board = layout.board;
  const sharedBox = new THREE.BoxGeometry(1, 1, 1);

  // ---- Backing plate -------------------------------------------------------
  const plate = new THREE.Mesh(
    new THREE.CylinderGeometry(board.plateRadius, board.plateRadius, board.plateThickness, 96, 1, false),
    createPlateMaterial(),
  );
  plate.rotation.x = -Math.PI / 2;
  plate.position.z = -board.plateThickness / 2;
  plate.name = 'plate';
  group.add(plate);

  // Rim around the plate edge.
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(board.plateRadius, 0.022, 10, 128),
    createNeonMaterial(0x4a6fa8, 0.5),
  );
  rim.position.z = -board.plateThickness / 2;
  rim.renderOrder = 5;
  group.add(rim);

  // ---- Maze walls ----------------------------------------------------------
  for (const mesh of buildWallMeshes(layout, sharedBox)) {
    group.add(mesh);
  }

  // ---- Pegs ----------------------------------------------------------------
  for (const obstacle of layout.obstacles) {
    const peg = new THREE.Mesh(
      new THREE.CylinderGeometry(obstacle.radius, obstacle.radius, obstacle.height, 20),
      createNeonMaterial(obstacle.color, obstacle.emissiveIntensity),
    );
    peg.rotation.x = -Math.PI / 2;
    peg.position.set(obstacle.x, obstacle.y, obstacle.z);
    peg.name = `obstacle:${obstacle.id}`;
    group.add(peg);
  }

  // ---- Translucent outer guard --------------------------------------------
  //
  // ONE TORUS ARC PER GUARD ARC, not a closed ring. The guard is the exit, so a
  // closed ring here would draw a wall across the opening the ball is meant to
  // leave through - the fallback would disagree with the colliders in the one
  // place where that matters most.
  for (const arc of layout.guardArcs) {
    const sweep = (arc.endDeg - arc.startDeg) * DEG2RAD;
    const mesh = new THREE.Mesh(
      new THREE.TorusGeometry(board.outerRadius, board.guardTube, 16, 96, sweep),
      createOuterRingMaterial(),
    );
    mesh.position.z = board.wallZ;
    mesh.rotation.z = arc.startDeg * DEG2RAD;
    mesh.name = `guard:${arc.id}`;
    mesh.renderOrder = 10;
    group.add(mesh);
  }

  // ---- Entry --------------------------------------------------------------
  //
  // There is deliberately NO entry port drawn on the board. The ball is fed in
  // from above at the top of the SCREEN and always appears at the same world
  // point, so a marker painted on the board would wander away from where the
  // ball actually appears as soon as the board was rotated. See
  // `ball.spawn` in blender/spec/maze.json.

  // ---- Centre hub ----------------------------------------------------------
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(board.hubRadius, board.hubRadius * 1.15, board.hubHeight, 32),
    createHubMaterial(),
  );
  hub.rotation.x = -Math.PI / 2;
  hub.position.z = board.hubHeight / 2;
  hub.name = 'hub';
  group.add(hub);

  // ---- Transparent front cover --------------------------------------------
  const cover = new THREE.Mesh(
    new THREE.CylinderGeometry(board.plateRadius, board.plateRadius, board.coverThickness, 96, 1, false),
    createCoverMaterial(),
  );
  cover.rotation.x = -Math.PI / 2;
  cover.position.z = board.coverZ;
  cover.name = 'cover';
  cover.renderOrder = 20;
  group.add(cover);

  return group;
}

/**
 * The scoring row, built procedurally.
 *
 * ---------------------------------------------------------------------------
 * THIS IS A FALLBACK, NOT THE SHIPPING PATH
 * ---------------------------------------------------------------------------
 * The row the player sees is `scoring_row.glb`. This builder is kept for the
 * same two reasons the board's fallback is kept: it is the recovery path when
 * the GLB is missing, and it is the reference the loaded model is measured
 * against - both are generated from the same spec table, so they cannot drift.
 *
 * Every box here mirrors one in `board.add_scoring_row()`:
 *
 *   floor    x1         the shared bottom
 *   dividers x(count+1) the pocket walls, INCLUDING the two end walls
 *   front lip x1        the glass the player watches through
 *   backs    x(count)   one coloured panel per pocket
 *
 * The lip is transparent on purpose. It has to be solid to the ball - it is what
 * stops a ball bouncing out of its pocket - but the player has to be able to see
 * which pocket the ball went into, so it is drawn as glass rather than as a
 * panel.
 */
export function buildFallbackScoreRow(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'scoring-row';
  const row = SCORE_ROW;

  const box = (
    name: string,
    material: THREE.Material,
    size: [number, number, number],
    position: [number, number, number],
    renderOrder = 0,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
    mesh.position.set(...position);
    mesh.name = name;
    mesh.renderOrder = renderOrder;
    group.add(mesh);
    return mesh;
  };

  const metal = createMetalMaterial(COLORS.rowDivider);
  const floorMaterial = createMatteMaterial(COLORS.rowFloor);
  const lipMaterial = createCoverMaterial();

  // Coloured back panels, one per pocket. Built per pocket rather than as one
  // wall with plates stuck on it: a plate proud of a wall is geometry the
  // colliders do not have, and this project has already been bitten once by
  // geometry that exists in only one of the two descriptions.
  for (const entry of slotsLeftToRight()) {
    box(
      `slot-back:${entry.id}`,
      createSlotPanelMaterial(slotTierColor(entry.tier)),
      [row.width, row.depth, row.divider],
      [entry.centerX, row.midY, row.backZ],
    );
  }

  // Dividers and the two end walls, as one series: `count + 1` plates at the
  // pocket boundaries, so the row is closed at both ends by construction.
  for (let index = 0; index <= row.count; index += 1) {
    const x = -row.span + row.width * index;
    box(`slot-divider:${index}`, metal, [row.divider, row.depth, row.zExtent], [x, row.midY, row.midZ]);
  }

  box('slot-floor', floorMaterial, [row.totalWidth, row.divider, row.zExtent], [0, row.floorY, row.midZ]);

  box(
    'slot-front-lip',
    lipMaterial,
    [row.totalWidth, row.depth, row.divider],
    [0, row.midY, row.frontZ],
    20,
  );

  return group;
}
